import { useQuery, useQueries } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { SchemaReviewsInvitationV1Service } from '../service';
import { ListInvitationTemplatesIO } from '../types';

export const getListInvitationTemplatesQueryKey = (req: ListInvitationTemplatesIO['input']) =>
  ['ReviewsInvitationV1', 'ListInvitationTemplates', req] as const;
type QueryKey = ReturnType<typeof getListInvitationTemplatesQueryKey>;

/**
 * A hook that returns a query for the `ListInvitationTemplates` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListInvitationTemplatesQuery = <E = unknown, D = ListInvitationTemplatesIO['output']>(
  req: ListInvitationTemplatesIO['input'],
  options?: LimitedSchemaQueryOptions<ListInvitationTemplatesIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<ListInvitationTemplatesIO['output'], E, D, QueryKey>({
    queryKey: getListInvitationTemplatesQueryKey(req),
    queryFn: () => SchemaReviewsInvitationV1Service.ListInvitationTemplates(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `ListInvitationTemplates` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useListInvitationTemplatesQueries = <E = unknown, D = ListInvitationTemplatesIO['output']>(
  argsArr: {
    req: ListInvitationTemplatesIO['input'];
    options?: LimitedSchemaQueryOptions<ListInvitationTemplatesIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getListInvitationTemplatesQueryKey(req),
      queryFn: () => SchemaReviewsInvitationV1Service.ListInvitationTemplates(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useListInvitationTemplatesQuery<E, D>>[];
